import { useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Crown, Check } from "lucide-react";

export const MemberClub = () => {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", mobile: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/members", form);
      if (data.already_member) {
        toast.info("You're already part of the Private Collection.");
      } else {
        toast.success("Welcome to the HYPPE AROMA Private Collection.");
      }
      setDone(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section data-testid="member-club-section" className="bg-[var(--ha-maroon)] text-[var(--ha-cream)] grain relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
        <div className="md:col-span-6">
          <p className="font-mono-label text-[10px] uppercase tracking-[0.3em] text-[var(--ha-gold)] mb-6 flex items-center gap-2">
            <Crown className="w-4 h-4" strokeWidth={1.5} /> Members Only
          </p>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] text-[var(--ha-cream)]">
            Join the HYPPE AROMA<br /><span className="italic gold-shimmer">Private Collection.</span>
          </h2>
          <p className="mt-8 max-w-md text-[var(--ha-cream)]/75 leading-relaxed">
            Be the first to discover new fragrance launches, limited editions, exclusive offers,
            fragrance education, and members-only releases.
          </p>
          <ul className="mt-8 space-y-3">
            {["Early access to launches", "Members-only limited editions", "Exclusive offers & education"].map((b) => (
              <li key={b} className="flex items-center gap-3 text-sm text-[var(--ha-cream)]/85">
                <Check className="w-4 h-4 text-[var(--ha-gold)]" strokeWidth={2} /> {b}
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-6 md:col-start-7">
          {done ? (
            <div data-testid="member-club-success" className="border border-[var(--ha-gold)]/40 bg-[var(--ha-cream)]/5 p-10 text-center">
              <Crown className="w-10 h-10 text-[var(--ha-gold)] mx-auto mb-4" strokeWidth={1.2} />
              <h3 className="font-serif-display text-3xl text-[var(--ha-cream)] mb-3">You're In.</h3>
              <p className="text-[var(--ha-cream)]/70">Welcome to the Private Collection. Watch your inbox for what's next.</p>
            </div>
          ) : (
            <form data-testid="member-club-form" onSubmit={submit} className="border border-[var(--ha-cream)]/15 bg-[var(--ha-cream)]/5 p-8 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <MemberField label="First Name" name="first_name" value={form.first_name} onChange={onChange} testid="member-first-name" required />
                <MemberField label="Last Name" name="last_name" value={form.last_name} onChange={onChange} testid="member-last-name" />
              </div>
              <MemberField label="Email Address" name="email" type="email" value={form.email} onChange={onChange} testid="member-email" required />
              <MemberField label="Mobile Number (Optional)" name="mobile" value={form.mobile} onChange={onChange} testid="member-mobile" />
              <button
                data-testid="member-join-btn"
                type="submit"
                disabled={sending}
                className="w-full px-8 py-4 border border-[var(--ha-gold)] bg-[var(--ha-gold)] text-[var(--ha-maroon)] font-mono-label text-xs uppercase tracking-[0.25em] hover:bg-[var(--ha-gold-deep)] hover:border-[var(--ha-gold-deep)] hover:text-[var(--ha-cream)] transition-colors disabled:opacity-60"
              >
                {sending ? "Joining…" : "Join the Collection"}
              </button>
              <p className="font-mono-label text-[9px] uppercase tracking-[0.2em] text-[var(--ha-cream)]/40 text-center">
                No spam · Unsubscribe anytime
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

const MemberField = ({ label, name, value, onChange, type = "text", testid, required }) => (
  <label className="block">
    <span className="font-mono-label text-[10px] uppercase tracking-[0.22em] text-[var(--ha-gold)]">{label}</span>
    <input
      data-testid={testid}
      type={type}
      name={name}
      required={required}
      value={value}
      onChange={onChange}
      className="mt-2 w-full border border-[var(--ha-cream)]/20 bg-[var(--ha-maroon)] px-3 py-2.5 text-[var(--ha-cream)] placeholder-[var(--ha-cream)]/30 focus:border-[var(--ha-gold)] focus:outline-none transition-colors"
    />
  </label>
);
